import { Company } from 'module/api/company/types';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { Layout } from 'module/ui/components/templates/argon/components';
import {
  faArrowsLeftRight,
  faSidebar,
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { useLocalStorageState } from 'ahooks';
import { AccountPeopleProfile } from 'api/account-people/types';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { WidthTransition } from 'styles/common';
import { ContentNavigator, Sections } from './content-navigator';
import { OpportunityLogSection } from './content-sections/oppurtunity-log';
import { RecentRisksSection } from './content-sections/recent-risks';
import { SignalSection } from './content-sections/signal';
import { UserPageSection } from './content-sections/user-page';
import { UserInformation } from './user-information/user-information';

const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;
const Sidebar = styled(Layout.Sider)`
  flex: 0 0 339px !important;
  max-width: 339px !important;
  min-width: 339px !important;
  width: 339px !important;
  height: 100% !important;
  overflow: hidden !important;
  overflow-y: auto !important;
  background: var(--white-100);
  padding: var(--spacing-05) var(--spacing-06);
  border-right: 1px solid var(--field-border-default);
`;

const RightSidebar = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-left: 1px solid var(--divider-default);
  overflow: hidden;
  gap: var(--spacing-01);
`;
const SidebarToggle = styled(Button)``;
const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  z-index: 103;
  padding: var(--spacing-03) var(--spacing-05);
`;

export const FullWidthMenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-02);
`;
const ContentWrapper = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  flex: 1;
  overflow: hidden;
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: var(--spacing-05) calc(var(--spacing-07) + 32px);
  align-items: center;
  overflow-y: auto;
`;
const CenterContent = styled.div<{ $isFullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  ${WidthTransition};
  ${({ $isFullWidth }) =>
    $isFullWidth
      ? css`
          max-width: 100%;
        `
      : css`
          max-width: 822px;
        `};
  width: 100%;
  gap: var(--spacing-09);
`;

interface PeopleProfileProps {
  userProfile: AccountPeopleProfile;
  company: Company;
}
export const PeopleProfile = ({ userProfile, company }: PeopleProfileProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isFullWidthActive, setIsFullWidthActive] =
    useLocalStorageState<boolean>('account_people_full_width');
  const [activeSection, setActiveSection] = useState<Sections>(
    Sections.CurrentRisks,
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollToSection = (section: Sections) => {
    const sectionElement = document.querySelector(
      `[data-section-key="${section}"]`,
    );

    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const handleSectionChange = (section: Sections) => {
    setActiveSection(section);
    scrollToSection(section);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) {
        return;
      }
      const containerRect = contentRef.current.getBoundingClientRect();
      const sectionElements =
        contentRef.current.querySelectorAll('[data-section-key]');
      let closestSection: string | null = null;
      let minDistance = Infinity;

      sectionElements.forEach((el) => {
        const rect = (el as HTMLElement).getBoundingClientRect();
        const distance = Math.abs(rect.top - containerRect.top);

        if (distance < minDistance) {
          minDistance = distance;
          closestSection = (el as HTMLElement).getAttribute('data-section-key');
        }
      });

      if (closestSection && closestSection !== activeSection) {
        setActiveSection(closestSection as Sections);
      }
    };

    const contentEl = contentRef.current;

    if (contentEl) {
      contentEl.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (contentEl) {
        contentEl.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeSection]);
  const toggleSidebarButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <PageWrapper>
      {isSidebarOpen ? (
        <Sidebar>
          <UserInformation userProfile={userProfile} company={company} />
        </Sidebar>
      ) : null}
      <ContentWrapper>
        <ActionBar>
          <SidebarToggle
            variant="ghost"
            leftIcon={<FontAwesomeIcon icon={faSidebar} size={12} />}
            onClick={() => {
              setIsSidebarOpen(!isSidebarOpen);
              setTimeout(() => {
                toggleSidebarButtonRef.current?.blur();
              }, 0);
            }}
            ref={toggleSidebarButtonRef}
          />
          <MoreActionsDropdown
            menu={{
              onClick: (e) => {
                e.domEvent.stopPropagation();
                e.domEvent.preventDefault();
                if (e.key === 'full-width') {
                  if (isFullWidthActive) {
                    setIsFullWidthActive(false);
                  } else {
                    setIsFullWidthActive(true);
                  }
                }
              },
              items: [
                {
                  key: 'full-width',
                  label: (
                    <FullWidthMenuItem>
                      <FormattedMessage id="full_width" />
                      <Switch size="small" checked={isFullWidthActive} />
                    </FullWidthMenuItem>
                  ),
                  icon: <FontAwesomeIcon icon={faArrowsLeftRight} size={12} />,
                },
              ],
            }}
            triggerElement={
              <Button
                variant="secondary"
                leftIcon={
                  <FontAwesomeIcon icon={faEllipsisVertical} size={12} />
                }
              />
            }
          />
        </ActionBar>
        <Content ref={contentRef}>
          <CenterContent $isFullWidth={isFullWidthActive}>
            <RecentRisksSection
              userProfile={userProfile}
              isFullWidth={isFullWidthActive}
            />
            <OpportunityLogSection
              userProfile={userProfile}
              isFullWidth={isFullWidthActive}
            />
            <UserPageSection
              userProfile={userProfile}
              company={company}
              isFullWidth={isFullWidthActive}
            />
            <SignalSection
              company={company}
              userProfile={userProfile}
              isFullWidth={isFullWidthActive}
            />
          </CenterContent>
        </Content>
      </ContentWrapper>
      <RightSidebar>
        <ContentNavigator
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          userProfile={userProfile}
        />
      </RightSidebar>
    </PageWrapper>
  );
};
